import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import FormModal from '@frontend/Components/Modals/FormModal';
import FormSelect from '@frontend/Components/Forms/FormSelect/FormSelect';
import FormInput from '@frontend/Components/Forms/FormInput/FormInput';
import EmployeeService from '@frontend/AppSettings/services/EmployeeService';
import type { SelectOption } from '@frontend/Models/Form/FormModels';

/**
 * Asked before a bid becomes a project — the two things the bid genuinely cannot answer.
 *
 * **Project lead.** `bids.estimator` is free text ("e.g. Jordan Avery") and `matters.employeeid` is
 * a UUID into `employees`; nothing bridges them, so every converted project showed a blank lead.
 * It is asked HERE rather than added to the bid because who RUNS a job is not always who priced it,
 * and conversion is the moment the answer is actually known.
 *
 * **Start date.** A bid has no start date at all — only nullable created/due dates — while
 * `workflow_project_instances.anchor_date` is NOT NULL and the whole schedule is dated off it.
 * The server defaults to today; this makes that visible and changeable instead of implicit.
 */
export interface ConvertToProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (opts: { employeeId?: string; anchorDate?: string }) => void;
  /** Disables the controls while the conversion is in flight. */
  converting?: boolean;
  bidName?: string;
}

const today = (): string => new Date().toISOString().split('T')[0];

export const ConvertToProjectModal = ({
  isOpen, onClose, onConfirm, converting = false, bidName,
}: ConvertToProjectModalProps): JSX.Element | null => {
  const [employeeId, setEmployeeId] = useState('');
  const [anchorDate, setAnchorDate] = useState(today);

  // Only fetched while the dialog is open — a list of every employee is not worth loading on a page
  // most visitors never convert from.
  const employees = useQuery({
    queryKey: ['Employees'],
    queryFn: EmployeeService.getEmployees,
    enabled: isOpen,
  });

  const options: SelectOption[] = useMemo(
    () => (employees.data ?? []).map((e: any) => ({
      value: e.EmployeeID,
      label: [e.FirstName, e.LastName].filter(Boolean).join(' ').trim() || e.EmployeeID,
    })),
    [employees.data],
  );

  if (!isOpen) return null;

  return (
    <FormModal isOpen={isOpen} onClose={onClose} title="Convert to Project" size="small">
      <p className="bid-panel-subtitle">
        {bidName ? `${bidName} will become a live project.` : 'This bid will become a live project.'}
        {' '}Its schedule is generated from the bid&apos;s phases, dated from the start date below.
      </p>

      <FormSelect
        label="Project Lead"
        name="employeeId"
        options={options}
        value={employeeId}
        onChange={setEmployeeId}
        placeholder="Select project lead..."
        isClearable
        isLoading={employees.isLoading}
        error={employees.error ? 'Could not load employees.' : undefined}
        disabled={converting}
        // Portal the menu over the dialog (plan C1): inside FormModal's scroll container a long
        // employee list overflowed the modal and the user scrolled the MODAL to reach the tail.
        portalMenu
      />

      <FormInput
        label="Start Date"
        name="anchorDate"
        type="date"
        value={anchorDate}
        onChange={(e) => setAnchorDate(e.target.value)}
        disabled={converting}
      />

      {/* A real modal footer (plan C2): actions right, one size — not the summary toolbar. */}
      <div className="bid-convert-actions">
        <button type="button" className="bid-detail-back-btn" onClick={onClose} disabled={converting}>
          Cancel
        </button>
        <button
          type="button"
          className="bid-detail-walk-btn"
          disabled={converting}
          onClick={() => onConfirm({
            // Both optional: the server defaults the anchor to today and leaves the lead unset,
            // which is exactly how every project converted before this dialog existed behaves.
            employeeId: employeeId || undefined,
            anchorDate: anchorDate || undefined,
          })}
        >
          {converting ? 'Converting…' : 'Convert'}
        </button>
      </div>
    </FormModal>
  );
};

export default ConvertToProjectModal;
